import { CheckCircle, Clock, Prohibit, WarningCircle } from "@phosphor-icons/react";

type ErrorKind = "expired" | "revoked" | "submitted" | "locked" | "unknown";

/**
 * The five ways a link can fail to open a form.
 *
 * `locked` is deliberately vague. Someone who has burned five guesses at the
 * identity question should learn nothing about what the right answer looked
 * like. `revoked` is treated the same as `expired` for the same reason: the
 * household does not need to know an admin killed the link on purpose.
 */
const CONTENT: Record<ErrorKind, { icon: typeof Clock; title: string; body: string }> = {
  expired: {
    icon: Clock,
    title: "This link has expired.",
    body: "Links stay valid for 14 days. Contact the Smart Climate team and we will send you a new one.",
  },
  revoked: {
    icon: Prohibit,
    title: "This link is no longer active.",
    body: "Contact the Smart Climate team and we will send you a new one.",
  },
  submitted: {
    icon: CheckCircle,
    title: "We already have your network details.",
    body: "Nothing more is needed from you. If your Wi-Fi has changed since you filled this in, contact the Smart Climate team and we will send a fresh link.",
  },
  locked: {
    icon: Prohibit,
    title: "This link is no longer active.",
    body: "Contact the Smart Climate team and we will send you a new one.",
  },
  unknown: {
    icon: WarningCircle,
    title: "We could not open this link.",
    body: "Check that you copied the whole address from your email. If it still does not work, contact the Smart Climate team.",
  },
};

export function LinkErrorState({ kind }: { kind: ErrorKind }) {
  const { icon: Icon, title, body } = CONTENT[kind];
  const isPositive = kind === "submitted";

  return (
    <main className="mx-auto flex min-h-[calc(100vh-105px)] max-w-[720px] items-center px-5 py-12 sm:px-8">
      <div className="w-full overflow-hidden rounded-[28px] bg-white p-6 text-center shadow-[0_24px_80px_-42px_rgba(40,35,31,0.5)] sm:p-12">
        <div
          className={`mx-auto flex size-16 items-center justify-center rounded-full ${
            isPositive ? "bg-success-subtle" : "bg-daylight-subtle"
          }`}
        >
          <Icon
            className={`size-9 ${isPositive ? "text-success" : "text-accent"}`}
            weight="light"
            aria-hidden
          />
        </div>
        <h1 className="text-primary mx-auto mt-6 max-w-[520px] text-[30px] leading-[1.14] tracking-[-0.02em] sm:text-[38px]">
          {title}
        </h1>
        <p className="text-muted mx-auto mt-4 max-w-[480px] text-[16px] leading-6">{body}</p>
      </div>
    </main>
  );
}

export type { ErrorKind };
